import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  parsePaymentAmount,
  sanitizeAmountInput,
  validatePaymentProofVerifyForm,
} from '../../utils/paymentProofFormState'
import { formatDateTime } from '../../utils/dateFormat'

function mapVerifyApiErrorToFields(message) {
  const msg = String(message || '').trim()
  if (!msg) return {}
  const lower = msg.toLowerCase()
  if (lower.includes('sap_doc_no')) {
    return { sapDocNo: 'SAP document number must be 9 digits and start with 1' }
  }
  if (lower.includes('received_amount') || lower.includes('received amount')) {
    return { receivedAmount: msg }
  }
  return {}
}

export default function PaymentProofVerifyDialog({
  open,
  request,
  onClose,
  onConfirm,
  submitting = false,
  requireSapDocNo = true,
  title = 'Verify payment proof',
}) {
  const [receivedAmount, setReceivedAmount] = useState('')
  const [sapDocNo, setSapDocNo] = useState('')
  const [verificationAt, setVerificationAt] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [varianceWarning, setVarianceWarning] = useState(null)
  const [submitError, setSubmitError] = useState(null)

  useEffect(() => {
    if (!open) return
    const requested = request?.requested_amount
    setReceivedAmount(
      requested != null && Number(requested) > 0 ? String(requested) : '',
    )
    setSapDocNo('')
    setVerificationAt(new Date())
    setFieldErrors({})
    setVarianceWarning(null)
    setSubmitError(null)
  }, [open, request?.id, request?.requested_amount, requireSapDocNo])

  const runValidation = () => {
    const { errors, varianceWarning: warning } = validatePaymentProofVerifyForm({
      receivedAmount,
      sapDocNo,
      requestedAmount: request?.requested_amount,
      requireSapDocNo,
    })
    setFieldErrors(errors)
    setVarianceWarning(warning)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    setSubmitError(null)
    if (!runValidation()) return
    const amount = parsePaymentAmount(receivedAmount)
    const verifiedAtSubmit = new Date()
    setVerificationAt(verifiedAtSubmit)
    try {
      await onConfirm({
        received_amount: amount,
        sap_doc_no: requireSapDocNo ? sapDocNo.trim() : sapDocNo.trim() || undefined,
        verified_at: verifiedAtSubmit.toISOString(),
      })
    } catch (err) {
      const message =
        err?.message ||
        (typeof err === 'string' ? err : 'Failed to verify payment proof request')
      const fieldFromApi = mapVerifyApiErrorToFields(message)
      if (Object.keys(fieldFromApi).length > 0) {
        setFieldErrors((prev) => ({ ...prev, ...fieldFromApi }))
      } else {
        setSubmitError(message)
      }
    }
  }

  const clearSubmitError = () => setSubmitError(null)

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 2, pt: 2, overflow: 'visible' }}>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {submitError ? (
            <Alert severity="error" onClose={clearSubmitError}>
              {submitError}
            </Alert>
          ) : null}
          {varianceWarning ? <Alert severity="warning">{varianceWarning}</Alert> : null}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Amount{' '}
              <Typography component="span" color="error.main">
                *
              </Typography>
            </Typography>
            <TextField
              value={receivedAmount}
              onChange={(e) => {
                clearSubmitError()
                setReceivedAmount(sanitizeAmountInput(e.target.value))
              }}
              error={Boolean(fieldErrors.receivedAmount)}
              helperText={fieldErrors.receivedAmount || 'Amount received in SAP'}
              fullWidth
              required
              placeholder="Enter amount received in SAP"
              inputProps={{ 'aria-label': 'Amount received in SAP' }}
            />
          </Box>
          <TextField
            label="Verification timestamp"
            value={verificationAt ? formatDateTime(verificationAt) : ''}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            helperText="Recorded automatically when you submit verification"
            fullWidth
          />
          {requireSapDocNo ? (
            <TextField
              label="SAP doc no"
              value={sapDocNo}
              onChange={(e) => {
                clearSubmitError()
                setSapDocNo(String(e.target.value || '').replace(/\D/g, '').slice(0, 9))
              }}
              InputLabelProps={{ shrink: true }}
              error={Boolean(fieldErrors.sapDocNo)}
              helperText={fieldErrors.sapDocNo || '9 digits, starts with 1'}
              fullWidth
              required
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ bgcolor: '#11809A', '&:hover': { bgcolor: '#0f6d82' } }}
        >
          {submitting ? 'Verifying…' : 'Submit verification'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
